import type { EditorView, PMNode, Slice } from '@bangle.io/prosemirror-plugins';

import {
  collectLinkTargets,
  isMarkdownContentPreserved,
} from './round-trip-check';
import { serializeClipboardMarkdown } from './serialize-slice-markdown';

/** MIME we set on Ctrl+C so paste can prefer Markdown over plain fallbacks. */
export const MARKDOWN_CLIPBOARD_MIME = 'text/markdown';

/**
 * Markdown for the live selection, matching
 * {@link PmEditorService.getSelectionMarkdown}.
 */
export function selectionMarkdown(
  view: EditorView,
  serialize: (doc: PMNode) => string,
): string | null {
  const { from, to, empty } = view.state.selection;
  if (empty) {
    return null;
  }
  return serialize(view.state.doc.cut(from, to));
}

/**
 * Puts Markdown on the clipboard and suppresses ProseMirror's default HTML
 * payload. Destinations prefer `text/html`, so shipping both made Ctrl+C paste
 * as rich text everywhere that mattered.
 */
export function writeMarkdownToClipboard(
  event: ClipboardEvent,
  markdownText: string,
): boolean {
  const data = event.clipboardData;
  if (!data) {
    return false;
  }
  data.clearData();
  data.setData('text/plain', markdownText);
  data.setData(MARKDOWN_CLIPBOARD_MIME, markdownText);
  event.preventDefault();
  return true;
}

/**
 * Reads Markdown written by {@link writeMarkdownToClipboard}. Returns null when
 * the clipboard still has HTML from another app — those pastes stay on the
 * rich-text path.
 */
export function readMarkdownFromClipboard(data: DataTransfer): string | null {
  const typed = data.getData(MARKDOWN_CLIPBOARD_MIME);
  if (typed.length > 0) {
    return typed;
  }
  if (data.getData('text/html').length > 0) {
    return null;
  }
  const plain = data.getData('text/plain');
  return plain.length > 0 ? plain : null;
}

/**
 * Parses clipboard Markdown into the selection using the same fidelity gate as
 * the "Paste from Markdown" command.
 */
export function insertClipboardMarkdown(
  view: EditorView,
  markdownText: string,
  parse: (source: string) => PMNode,
): boolean {
  let parsed: PMNode;
  try {
    parsed = parse(markdownText);
    if (!isMarkdownContentPreserved(markdownText, collectLinkTargets(parsed))) {
      return false;
    }
  } catch {
    return false;
  }

  const inline =
    parsed.childCount === 1 && parsed.firstChild?.type.name === 'paragraph';
  const slice = inline
    ? parsed.slice(1, Math.max(1, parsed.content.size - 1))
    : parsed.slice(0, parsed.content.size);
  if (slice.size === 0) {
    return false;
  }

  const tr = view.state.tr.replaceSelection(slice);
  if (!tr.docChanged) {
    return false;
  }
  view.dispatch(tr.scrollIntoView());
  return true;
}

export function createMarkdownClipboardPluginProps(options: {
  serialize: (doc: PMNode) => string;
  parse: (source: string) => PMNode;
}): {
  clipboardTextSerializer: (slice: Slice, view: EditorView) => string;
  handleDOMEvents: {
    copy: (view: EditorView, event: Event) => boolean;
    cut: (view: EditorView, event: Event) => boolean;
  };
  handlePaste: (view: EditorView, event: Event) => boolean;
} {
  const { serialize, parse } = options;

  return {
    // Drag / serializeForClipboard still ask for text; keep it Markdown.
    clipboardTextSerializer(slice, view) {
      return serializeClipboardMarkdown(slice, view, serialize);
    },
    handleDOMEvents: {
      copy(view, event) {
        if (!isClipboardEvent(event)) {
          return false;
        }
        const markdownText = selectionMarkdown(view, serialize);
        if (markdownText == null) {
          return false;
        }
        return writeMarkdownToClipboard(event, markdownText);
      },
      cut(view, event) {
        if (!isClipboardEvent(event)) {
          return false;
        }
        const markdownText = selectionMarkdown(view, serialize);
        if (markdownText == null) {
          return false;
        }
        if (!writeMarkdownToClipboard(event, markdownText)) {
          return false;
        }
        if (view.editable) {
          view.dispatch(
            view.state.tr
              .deleteSelection()
              .scrollIntoView()
              .setMeta('uiEvent', 'cut'),
          );
        }
        return true;
      },
    },
    handlePaste(view, event) {
      if (!isClipboardEvent(event) || !event.clipboardData) {
        return false;
      }
      const markdownText = readMarkdownFromClipboard(event.clipboardData);
      if (markdownText == null) {
        return false;
      }
      return insertClipboardMarkdown(view, markdownText, parse);
    },
  };
}

function isClipboardEvent(event: Event): event is ClipboardEvent {
  return 'clipboardData' in event;
}
